import type {
  CollectionMutationDetailResponse,
  CollectionPaymentProofDetailResponse,
} from '@diamond/shared/types';
import { formatCurrencyIdr } from '@diamond/shared/utils';

import type {
  LHCDetailInvoiceRowData,
  SearchNomorMutasiRowData,
} from '../../../types';

const FALLBACK_VALUE = '-';

const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.webp', '.gif'] as const;

type OrderPayment =
  CollectionMutationDetailResponse['order_payments'][number];

export type PaymentProofPreviewKind = 'image' | 'pdf' | 'unsupported';

const toDisplayValue = (value: string | null | undefined): string =>
  value && value.trim().length > 0 ? value : FALLBACK_VALUE;

function getProofPathname(url: string): string {
  try {
    return new URL(url).pathname.toLowerCase();
  } catch {
    return url.split('?')[0].toLowerCase();
  }
}

export function getPaymentProofPreviewKind(
  url: string | null | undefined
): PaymentProofPreviewKind | null {
  if (!url || url.trim().length === 0) {
    return null;
  }

  const pathname = getProofPathname(url);

  if (pathname.endsWith('.pdf')) {
    return 'pdf';
  }

  if (IMAGE_EXTENSIONS.some((ext) => pathname.endsWith(ext))) {
    return 'image';
  }

  return 'unsupported';
}

export function findFirstPaymentProofUrl(
  orderPayments: OrderPayment[] | undefined
): string | null {
  if (!orderPayments?.length) {
    return null;
  }

  for (const payment of orderPayments) {
    const url = payment.payment_proofs?.[0]?.url;
    if (url && url.trim().length > 0) {
      return url;
    }
  }

  return null;
}

export function findOrderPaymentWithProof(
  orderPayments: OrderPayment[] | undefined
): OrderPayment | null {
  if (!orderPayments?.length) {
    return null;
  }

  return (
    orderPayments.find(
      (payment) => (payment.payment_proofs?.[0]?.url?.trim().length ?? 0) > 0
    ) ??
    orderPayments[0] ??
    null
  );
}

export interface MutationDetailDrawerViewModel {
  hasContent: boolean;
  mutationNumber: string;
  poNumber: string;
  poAmount: string;
  overPayment: string;
  // TODO: relatedTransactions needs status field from backend to support "Batal" display
  relatedTransactions: CollectionMutationDetailResponse['transactions'];
  orderPaymentCount: number;
  paymentProofCount: number;
  proofImageUrl: string | null;
  proofPreviewKind: PaymentProofPreviewKind | null;
  bankAccount: string;
  bankName: string;
}

export const getMutationDetailDrawerViewModel = ({
  mutationDetailData,
  paymentProofDetailData,
  rowData,
  selectedMutation,
}: {
  mutationDetailData?: CollectionMutationDetailResponse | null;
  paymentProofDetailData?: CollectionPaymentProofDetailResponse | null;
  rowData: LHCDetailInvoiceRowData | null;
  selectedMutation: SearchNomorMutasiRowData | null;
}): MutationDetailDrawerViewModel => {
  const detailData = mutationDetailData ?? paymentProofDetailData ?? null;
  const orderPayments = detailData?.order_payments;
  const paymentWithProof = findOrderPaymentWithProof(orderPayments);
  const firstOrderPayment = orderPayments?.[0] ?? null;
  const displayPayment = paymentWithProof ?? firstOrderPayment;
  const proofImageUrl = findFirstPaymentProofUrl(orderPayments);
  const paymentProofCount =
    orderPayments?.reduce(
      (count, payment) => count + (payment.payment_proofs?.length ?? 0),
      0
    ) ?? 0;

  return {
    hasContent: Boolean(detailData || selectedMutation || rowData),
    mutationNumber:
      selectedMutation?.mutationNo ??
      mutationDetailData?.bank_mutation_number ??
      toDisplayValue(rowData?.bankMutationNo),
    poNumber: detailData?.po_number ?? toDisplayValue(rowData?.poNumber),
    poAmount: detailData
      ? formatCurrencyIdr(detailData.po_amount)
      : toDisplayValue(rowData?.poValue),
    overPayment: toDisplayValue(selectedMutation?.remaining),
    relatedTransactions: detailData?.transactions ?? [],
    orderPaymentCount: orderPayments?.length ?? 0,
    paymentProofCount,
    proofImageUrl,
    proofPreviewKind: getPaymentProofPreviewKind(proofImageUrl),
    bankAccount: toDisplayValue(displayPayment?.bank_account),
    bankName:
      displayPayment?.bank_name ?? toDisplayValue(selectedMutation?.bank),
  };
};
